import { useFormikContext } from 'formik';
import { pluck } from '../../helpers/pluck';
import CreatableSelect from 'react-select/creatable';
import { StylesConfig } from 'react-select';
import { useState, useCallback } from 'react';

export type Option = {
  value: string;
  label: string;
};

export type ComboBoxFormikProps = {
  name: string;
  label?: string;
  options: Option[];
  className?: string;
  required?: boolean;
  onChange?: (value: string) => void;
  disabled?: boolean;
  defaultValue?: Option | null;
  value?: string; // Valor controlado opcional
  mask?: (input: string) => string; // Função para aplicar máscara no input digitado
};

const inputStyles: StylesConfig<Option, false> = {
  control: (provided) => ({ ...provided, borderColor: '#616161', height: "42px" }),
};

//eslit-disable-next-line @typescript-eslint/no-explicit-any
interface FormValues {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

const ComboBox: React.FC<ComboBoxFormikProps> = ({
  name,
  label,
  options,
  disabled,
  className,
  required,
  onChange,
  value,
  mask,
}) => {
  const { setFieldValue, values, errors } = useFormikContext<FormValues>();

  // Se for passado via prop "value", usa-o; senão, extrai do Formik.
  const selectedValue: string = value || pluck(name, values) || "";

  // Estado para controlar o input interno (o texto digitado)
  const [inputValue, setInputValue] = useState("");

  // Estado interno para armazenar as opções criadas pelo usuário
  const [createdOptions, setCreatedOptions] = useState<Option[]>([]);

  const combinedOptions = [...options, ...createdOptions];

  const handleSelectChange = useCallback(
    (option: Option | null) => {
      const newValue = option ? option.value : "";
      setFieldValue(name, newValue);
      if (onChange) {
        onChange(newValue);
      }
      // Limpa o input de digitação após a seleção
      setInputValue("");
    },
    [name, onChange, setFieldValue]
  );

  const handleInputChange = useCallback(
    (newInputValue: string) => {
      let maskedValue = newInputValue;
      if (mask) {
        maskedValue = mask(newInputValue);
      }
      setInputValue(maskedValue);
      return maskedValue;
    },
    [mask]
  );

  // Quando o usuário cria uma nova opção, ela é adicionada à lista
  const handleCreateOption = useCallback(
    (inputValue: string) => {
      const newOption: Option = { label: inputValue, value: inputValue };
      setCreatedOptions((prev) => [...prev, newOption]);
      // Define o valor criado no Formik
      setFieldValue(name, newOption.value);
      if (onChange) {
        onChange(newOption.value);
      }
      // Limpa o input
      setInputValue("");
    },
    [name, onChange, setFieldValue]
  );

  // Se o valor selecionado não existir na lista de opções, crie um objeto para exibição
  const selectedOption: Option | null = selectedValue
    ? combinedOptions.find((opt) => opt.value === selectedValue) ||
    { label: selectedValue, value: selectedValue }
    : null;

  return (
    <div className={`mb-2 ${className}`}>
      <label htmlFor={name} className="block font-medium text-[16px] text-gray-600">
        {label || ''} {required ? <b className="text-redPrimary">*</b> : ''}
      </label>
      <CreatableSelect
        id={name}
        name={name}
        value={selectedOption}
        onChange={handleSelectChange}
        options={combinedOptions}
        isClearable
        isDisabled={disabled}
        placeholder="Selecione ou digite..."
        styles={inputStyles}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onCreateOption={handleCreateOption}
        formatCreateLabel={(input) => `Criar "${input}"`} // Opcional: personaliza o rótulo da opção de criação
      />
      {pluck(name, errors) && (
        <small className="text-redPrimary/90" aria-live="polite">
          {pluck(name, errors)}
        </small>
      )}
    </div>
  );
};

export default ComboBox;
